/**
 * Server-side leader data fetching utilities
 * Use these in server components and API routes
 */

import { DeckIdentity } from "@/interfaces/DeckIdentity";
import { getLeaderSquare } from "@/lib/getLeaderImage";

type LeaderData = DeckIdentity;
type Leaders = Record<string, { name: string; color: string }>;

/**
 * Fetch all leaders from tcgfan-backend /api/data/leaders (server-side)
 * @returns Promise of leaders object
 */
export async function fetchLeadersServer(): Promise<Leaders> {
  try {
    const apiUrl =
      process.env.BACKEND_API_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      "http://localhost:3001";
    const secretKey = process.env.BACKEND_SECRET_KEY;

    if (!secretKey) {
      console.error("BACKEND_SECRET_KEY not configured");
      return {};
    }

    const params = new URLSearchParams({
      game: "one-piece",
    });

    const response = await fetch(
      `${apiUrl}/api/data/leaders?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${secretKey}`,
        },
        next: { revalidate: 3600 }, // Cache for 1 hour
      },
    );

    if (!response.ok) {
      console.error(`Failed to fetch leaders: ${response.status}`);
      return {};
    }

    const leaders = await response.json();

    // Backend already returns in the correct format
    return leaders;
  } catch (error) {
    console.error("Error fetching leaders:", error);
    return {};
  }
}

/**
 * Get leader data by ID (server-side)
 * @param leaderId - The leader card ID
 * @returns Promise of leader data
 */
export async function getLeaderDataServer(
  leaderId: string,
): Promise<LeaderData> {
  const leaders = await fetchLeadersServer();
  const normalizedId = leaderId.toUpperCase();

  return {
    ...leaders[normalizedId],
    id: normalizedId,
    image: getLeaderSquare(normalizedId),
  };
}

/**
 * Get all leader IDs (server-side)
 * @returns Promise of leader ID array
 */
export async function getAllLeaderIdsServer(): Promise<string[]> {
  const leaders = await fetchLeadersServer();
  return Object.keys(leaders).filter((id) => !id.includes("_p"));
}

/**
 * Get all leader data (server-side)
 * @returns Promise of leader data array
 */
export async function getLeaderDataAllServer(): Promise<LeaderData[]> {
  const leaders = await fetchLeadersServer();
  const leaderData = [];

  for (const [id, data] of Object.entries(leaders)) {
    if (!id.includes("_p")) {
      leaderData.push({
        ...data,
        id: id,
      });
    }
  }

  leaderData.sort((a, b) => a.name.localeCompare(b.name));
  return leaderData;
}
